import { Box } from '@mui/material';
import FeedPromptsModalTextFieldInput from 'apps/studio/studio-tab/components/feed-prompts-modal/input-comp/InputComponents';
import BasicInfo from 'apps/studio/studio-tab/components/image-info/components/BasicInfo';
import ModelInfo from 'apps/studio/studio-tab/components/image-info/components/ModelInfo';
import StyleInfo from 'apps/studio/studio-tab/components/image-info/components/StyleInfo';
import { imageInfoStyles } from 'apps/studio/studio-tab/components/image-info/ImageInfoStyles';
import * as loadingAnimationData from 'core/assets/lottie/green-loader-animation.json';

import { ReactComponent as Expand } from 'core/assets/svg/export-image/expand.svg';
import { ReactComponent as Info } from 'core/assets/svg/studio-tab/feed-prompts-modal/info.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseImage from 'core/components/image/BaseImage';
import BaseTypography from 'core/components/typography/BaseTypography';
import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import { IconButton } from 'core/layouts/components/export-image-buttons/ExportImageButtons';
import LottieAnimation from 'core/layouts/components/lottie-animation/LottieAnimation';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { ConditionalLayer } from 'core/layouts/ui-blocks/ReusableComponents';
import { reusableStyles, tabletBreakpoint } from 'core/layouts/ui-blocks/ReusableStyles';
import {
	feedPromptModalDetailsSelector,
	feedPromptsModalTypeSelector,
	publishPromptAsync,
	setShowConfirmPromptScreen,
	showConfirmPromptScreenSelector,
} from 'core/store/reducers/appReducer/AppSlice';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const styles = imageInfoStyles;

function ImageInfo(props, ref) {
	const { handleClose, contentWrapperStyles, onClickExpandImage, isImgToImg } = props;
	const showConfirmPromptScreen = useSelector(showConfirmPromptScreenSelector);

	const [nameInput, setNameInput] = useState('');
	const [keywordsInput, setKeywordsInput] = useState('');
	const [hidePrompt, setHidePrompt] = useState(true);
	const [hideNegativePrompt, setHideNegativePrompt] = useState(true);

	useEffect(() => {
		if (showConfirmPromptScreen) {
			setNameInput('');
			setKeywordsInput('');
		}
	}, [showConfirmPromptScreen]);

	useImperativeHandle(
		ref,
		() => ({
			reset() {
				setHidePrompt(true);
				setHideNegativePrompt(true);

				const element = document.getElementById(ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR);
				if (element) {
					element.scrollIntoView({ block: 'end', behavior: 'smooth' });
				}
			},
		}),
		[],
	);

	const dispatch = useDispatch();

	const selectedPrompt = useSelector(feedPromptModalDetailsSelector);
	const feedPromptsModalType = useSelector(feedPromptsModalTypeSelector);
	return (
		<Box
			id="content-wrapper"
			sx={[
				styles.contentWrapper,
				...(Array.isArray(contentWrapperStyles) ? contentWrapperStyles : [contentWrapperStyles]),
			]}
			onClick={(e) => e.stopPropagation()}
		>
			{!showConfirmPromptScreen && (
				<Box
					id="detail-screen-content"
					sx={[
						styles.detailScreenContent,
						{
							flexDirection: { xs: 'column', [tabletBreakpoint]: 'row' },
						},
					]}
				>
					<Box id="image-wrapper" sx={[styles.detailScreenImgWrapper, { alignSelf: 'center' }]}>
						<Box id="image-cont" sx={styles.detailScreenImgCont}>
							<Box id="image" sx={styles.image}>
								{selectedPrompt.loading ? (
									<Box sx={{ height: '90px', width: '180px' }}>
										<LottieAnimation animationData={loadingAnimationData} loop />
									</Box>
								) : (
									<BaseImage src={selectedPrompt?.thumbnail} alt="" />
								)}
							</Box>
							<Box
								id="image-buttons-cont"
								sx={{
									position: 'absolute',
									top: 0,
									right: 0,
									padding: '16px',
								}}
							>
								<IconButton onClick={onClickExpandImage} icon={<Expand />} />
							</Box>
						</Box>
					</Box>
					<ConditionalLayer isShown={!isImgToImg}>
						{selectedPrompt.styleId ? (
							<StyleInfo
								selectedPrompt={selectedPrompt}
								handleClose={handleClose}
								feedPromptsModalType={feedPromptsModalType}
								hidePrompt={hidePrompt}
								setHidePrompt={setHidePrompt}
								hideNegativePrompt={hideNegativePrompt}
								setHideNegativePrompt={setHideNegativePrompt}
							/>
						) : (
							<ModelInfo
								selectedPrompt={selectedPrompt}
								handleClose={handleClose}
								feedPromptsModalType={feedPromptsModalType}
								hidePrompt={hidePrompt}
								setHidePrompt={setHidePrompt}
								hideNegativePrompt={hideNegativePrompt}
								setHideNegativePrompt={setHideNegativePrompt}
							/>
						)}
					</ConditionalLayer>
					<ConditionalLayer isShown={isImgToImg}>
						<BasicInfo
							handleClose={handleClose}
							selectedPrompt={selectedPrompt}
							feedPromptsModalType={feedPromptsModalType}
						/>
					</ConditionalLayer>
				</Box>
			)}
			{showConfirmPromptScreen && (
				<Box id="confirm-screen-content-wrapper" sx={[styles.confirmScreenWrapper]}>
					<Box sx={{ padding: '16px 24px' }}>
						<BaseTypography variant="body1" styles={{ color: 'colors.tonal1' }}>
							Prompt details
						</BaseTypography>
					</Box>
					<Box id="confirm-screen-content" sx={[styles.confirmScreenContent]}>
						<Box id="details-cont" sx={styles.confirmScreenPromptDetailsCont}>
							<Box
								id="details-scroll-wrapper"
								sx={[
									styles.promptDetailsScrollWrapper,
									reusableStyles.verticalScrollBarStyles,
									{
										'&::-webkit-scrollbar-track': { backgroundColor: 'colors.dark6' },
										'&::-webkit-scrollbar-thumb': { border: `4px solid ${colorPalette.dark6}` },
									},
								]}
							>
								<Box id="prompt-details" sx={styles.confirmScreenPromptDetails}>
									<Box
										id="prompt-section"
										sx={[reusableStyles.flexCol, { alignItems: 'start', gap: '4px' }]}
									>
										<BaseTypography variant="b2_caption_medium" styles={{ color: 'colors.light3' }}>
											Prompt
										</BaseTypography>
										<BaseTypography variant="b2_caption" styles={{ color: 'colors.light1' }}>
											{selectedPrompt.prompt}
										</BaseTypography>
									</Box>

									<Box
										id="model-section"
										sx={[
											reusableStyles.flexRow,
											{
												justifyContent: 'space-between',
												gap: '4px',
											},
										]}
									>
										<BaseTypography variant="b2_caption_medium" styles={{ color: 'colors.light3' }}>
											Model
										</BaseTypography>
										<Box sx={styles.modelDetail}>
											<Box sx={styles.modelImage}>
												<BaseImage src={selectedPrompt.modelImage} alt="" />
											</Box>
											<BaseTypography variant="b2_caption" styles={{ color: 'colors.light1' }}>
												{selectedPrompt.modelLabel}
											</BaseTypography>
										</Box>
									</Box>
									<Box id="prompt-details-divider" sx={styles.divider} />

									<Box id="auto-assign-section" sx={[reusableStyles.flexRow, { gap: '8px' }]}>
										<Info />
										<BaseTypography variant="b2_caption_medium" styles={{ color: 'colors.light4' }}>
											Our AI will auto assign it if you leave it blank
										</BaseTypography>
									</Box>

									<FeedPromptsModalTextFieldInput
										id="name"
										title="Name"
										subtitle="Suggest a title for this prompt"
										placeholder="Example: A cute dog"
										value={nameInput}
										onChangeFunc={(e) => setNameInput(e.target.value)}
									/>

									<FeedPromptsModalTextFieldInput
										id="name"
										title="Keywords"
										subtitle="Enter at least five words related to your image separated by comma"
										placeholder="Example: anime, photoreal, animal ... "
										value={keywordsInput}
										onChangeFunc={(e) => setKeywordsInput(e.target.value)}
									/>
								</Box>
							</Box>
							<Box id="confirm-cont-wrapper" sx={styles.confirmScreenButtonsCont}>
								<BaseButton
									id="confirm-publish-prompt"
									styles={[styles.buttonStyles, styles.publishPromptButton]}
									onClick={(e) => {
										e.stopPropagation();
										dispatch(
											publishPromptAsync({
												nameInput,
												keywordsInput,
											}),
										);
									}}
								>
									<BaseTypography variant="b1_bold" styles={{ color: 'colors.dark6' }}>
										Confirm
									</BaseTypography>
								</BaseButton>
								<BaseButton
									id="cancel-publish-prompt"
									styles={[styles.buttonStyles, styles.publishPromptButton]}
									onClick={(e) => {
										e.stopPropagation();
										dispatch(setShowConfirmPromptScreen(false));
									}}
								>
									<BaseTypography variant="b1_bold" styles={{ color: 'colors.dark6' }}>
										Cancel
									</BaseTypography>
								</BaseButton>
							</Box>
						</Box>

						<Box id="image-wrapper" sx={styles.confirmScreenImgWrapper}>
							<Box id="image-cont" sx={styles.confirmScreenImgCont}>
								<Box id="image" sx={styles.image}>
									<BaseImage src={selectedPrompt?.image} alt="" />
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	);
}

export default React.forwardRef(ImageInfo);
